import { last } from 'lodash';
import moment from 'moment';
import {
  SUBSCRIPTION_PAYMENT_TYPES,
  ORG_PATHS,
  ENABLE_ORG_MANAGEMENT,
} from '../constants';
import intlMessages from '../actions/lang/en.json';
import { injectParams } from './pathHelpers';
import { toNormalCase } from '../utils/tableUtils';

export const getSubName = (sub) => sub.items[0]?.description ?? 'Unknown';
export const getSubNameFromItems = (items) =>
  items[0]?.description ?? 'Unknown';

export const formatDate = (date) => {
  const dateArray = date.split('-');
  return `${dateArray[1]}/${dateArray[2]}/${dateArray[0]}`;
};

export const formatRecurrenceDuration = (duration) => {
  switch (duration) {
    case 'Years':
      return 'yearly';
    case 'Months':
      return 'monthly';
    default:
      return duration;
  }
};

export function formatPaymentMethod(paymentMethod) {
  if (paymentMethod.cardNumberMask) {
    return {
      id: paymentMethod?.id,
      cardType: paymentMethod?.cardType || null,
      cardholderName: paymentMethod?.cardholderName,
      lastFour: paymentMethod?.cardNumberMask,
      expMonth: paymentMethod?.expirationMonth,
      expYear: paymentMethod?.expirationYear,
      paymentMethodType: 'CreditCard',
    };
  } else {
    return {
      id: paymentMethod?.id,
      billingAccountId: paymentMethod?.billingAccountId,
      account: paymentMethod?.accountNumber,
      paymentMethodType: 'Account',
    };
  }
}

export const formatLastFour = (cardMask) =>
  cardMask?.substr(cardMask.length - 4);

export const formatPayment = (paymentMethod) => {
  if (paymentMethod == null) {
    return 'Unknown';
  }
  if (paymentMethod.account) {
    return `Billing account for ${
      paymentMethod.organizationName
        ? paymentMethod.organizationName
        : paymentMethod.account
    }`;
  }
  if (paymentMethod.tokenLastThree) {
    return 'Paypal account';
  }
  const cardType = paymentMethod.cardType ? toNormalCase(paymentMethod.cardType) : 'Unknown';
  return `${cardType} ending in ${paymentMethod.lastFour}`;
};

export const findMatchingPaymentMethod = (value, creditCards, billingAccs) => {
  const payment = value || '';
  const creditCard = creditCards.find(
    (card) => formatLastFour(card.id) === formatLastFour(payment?.id || '')
  );

  if (creditCard) {
    return {
      paymentMethod: creditCard,
      type: SUBSCRIPTION_PAYMENT_TYPES.CREDIT_CARD,
    };
  }

  const account = billingAccs.find(
    (acct) => acct.accountNumber === payment.accountNumber
  );
  return { paymentMethod: account, type: SUBSCRIPTION_PAYMENT_TYPES.ACCOUNT };
};

export const legacyFindMatchingPaymentMethod = (
  value,
  creditCards,
  billingAccs
) => {
  const payment = value || '';
  const creditCard = creditCards.find(
    (card) => formatLastFour(card?.cardNumberMask) === formatLastFour(payment)
  );

  if (creditCard) {
    return {
      paymentMethod: creditCard,
      type: SUBSCRIPTION_PAYMENT_TYPES.CREDIT_CARD,
    };
  }

  const account = billingAccs.find(
    (acct) => acct.accountNumber === payment.accountNumber
  );
  return { paymentMethod: account, type: SUBSCRIPTION_PAYMENT_TYPES.ACCOUNT };
};

export const formatOrderLink = (sub) => {
  const orderId = last(sub.ordersPlaced).id;

  if (sub.paymentMethod?.billingAccountId) {
    const { organizationId, billingAccountId } = sub.paymentMethod;
    return ENABLE_ORG_MANAGEMENT
      ? injectParams(ORG_PATHS.ORDER_DETAILS, {
          organizationId,
          billingAccountId,
          orderId,
        })
      : `org-management/billing-account/${billingAccountId}/order-history/order/${orderId}`;
  } else {
    return `order-history/order/${orderId}`;
  }
};

export const showExpiryMessage = (
  { paymentMethod, items = [], failedRenewals },
  billingAccs
) => {
  const name = getSubNameFromItems(items);

  if (failedRenewals) {
    //if failedRenewal then return with failed renewal message
    return {
      show: true,
      name,
      message: `${intlMessages.Errors.PaymentMethods.Failed_Renewal_Attempt_1} ${failedRenewals.length} ${intlMessages.Errors.PaymentMethods.Failed_Renewal_Attempt_2}`,
    };
  }

  if (isExpired(paymentMethod?.expMonth, paymentMethod?.expYear)) {
    //if expired card then return with expired card message
    return {
      show: true,
      name,
      message: intlMessages.Errors.PaymentMethods.Invalid_Credit_Card,
    };
  }
  if (paymentMethod?.billingAccountId && billingAccs) {
    // find billing acc in array and check pinValidationRequired
    const billingAcc = billingAccs.find(
      (acc) => acc.billingAccountId === paymentMethod?.billingAccountId
    );
    const showMessage = billingAcc ? billingAcc.pinValidationRequired : false;

    return {
      show: showMessage,
      name,
      message: showMessage
        ? intlMessages.Errors.PaymentMethods.Billing_Account_PIN_Reset
        : '',
    };
  }
  if (paymentMethod?.cardType) {
    // check if expMonth & expYear are in the past or within 30 days
    const expDate = moment(
      `${paymentMethod?.expMonth}/${paymentMethod?.expYear}`,
      'MM/YYYY'
    );
    const thirtyOneDays = moment().add(31, 'days');
    const showMessage = expDate.isBefore(thirtyOneDays);

    return {
      show: showMessage,
      name,
      message: showMessage
        ? intlMessages.Errors.PaymentMethods.Expiring_Credit_Card
        : '',
    };
  }
  return { show: false, name, message: '' };
};

export const isExpired = (expMonth, expYear) => {
  const expDate = moment(`${expMonth}/${expYear}`, 'MM/YYYY');
  const today = moment();

  return expDate.isBefore(today);
};

export const sortSubs = (...subs) => {
  return subs.sort(function (a, b) {
    return new Date(a.scheduledOrderDate) - new Date(b.scheduledOrderDate);
  });
};

export const isInactive = (sub) => {
  if (sub.status === 'Inactive') {
    return true;
  }
  return false;
};

export function filterValidBillingAccounts(
  billingAccounts,
  organizationId,
  isOrgSubs
) {
  if (isOrgSubs) {
    return billingAccounts?.filter(
      (acct) => acct.billingAddress && acct.organizationId === organizationId
    ).filter(acct => acct.allowCheckout === true);
  } else {
    return billingAccounts?.filter((acct) => acct.billingAddress).filter(acct => acct.allowCheckout === true);
  }
}

export function formatShippingAddress(address){
  return {
    id: address.id,
    addressType: address.addressType,
    firstName: address.firstName,
    lastName: address.lastName,
    line1: address.streetAddress.line1,
    line2: address.streetAddress?.line2,
    city: address.streetAddress.city,
    state: address.streetAddress.countrySubdivision,
    zip: address.streetAddress.postalCode,
    country: address.streetAddress.countryCode,
    phone: address.contact.phoneNumber,
    organization: address?.contact?.recipient?.OrganizationRecipient?.organizationName,
  };
}
